import { useEffect } from 'react';
import {
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  Task,
  TaskTimelineFrameEnum,
  TaskTimelineGroupByEnum,
  useTasksTimelineQuery,
} from 'graphql-common';
import { transformDate } from '@lib/utils/dateTimeHelpers';
import {
  getPeriodEndDate,
  getTimelineWindow,
} from 'routes/Dashboard/TasksTimeline/utils/getPeriodDate';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { TASKS_TIMELINE_FILTER_EMPTY_VALUES } from 'routes/Dashboard/TasksTimeline/utils/useFilterFields';
import { SelectOption } from '@lib/components/Select/types';
import { captureException } from '@lib/utils/captureException';
import { ListQueryHookProps } from '../types';

export default function useDashboardTasksCompletionsQueryHook({
  filterParams = {},
  loading: defaultLoading,
  skip,
  tasks,
}: ListQueryHookProps & { tasks: Task[] }) {
  const timelineView = (filterParams[TasksFilterFieldNames.TimelineView] ||
    TASKS_TIMELINE_FILTER_EMPTY_VALUES[
      TasksFilterFieldNames.TimelineView
    ]) as TaskTimelineFrameEnum;
  const validityHintsParam = filterParams[
    TasksFilterFieldNames.ValidityHints
  ] as SelectOption[];
  const validityHints = validityHintsParam?.map(
    ({ value }) => value as FormTemplateQuestionFieldOptionsValidityHintEnum,
  );
  let endDateParam = filterParams[
    TasksFilterFieldNames.TimelineEndDate
  ] as string;
  if (!endDateParam) {
    const nowDate = new Date();
    // CET-anchored (transformDate), not a bare `yyyy-MM-dd`: `endDateParam`
    // feeds `getTimelineWindow` below, which parses via
    // `revertTransformDate` and would drop a day for timezones east of CET.
    endDateParam = transformDate(
      getPeriodEndDate(nowDate.toISOString(), timelineView),
      true,
    );
  }
  const ids = tasks.map(({ id }) => id);

  // Same window the rows and stat cards filter on (see useActions).
  const dateRange = getTimelineWindow(endDateParam, timelineView);
  let groupBy = TaskTimelineGroupByEnum.Day;
  if (
    TaskTimelineFrameEnum.Year === timelineView ||
    TaskTimelineFrameEnum.Quarter === timelineView
  ) {
    groupBy = TaskTimelineGroupByEnum.Month;
  }
  const { data, loading, error } = useTasksTimelineQuery({
    skip: skip || !tasks.length || !endDateParam || defaultLoading,
    fetchPolicy: 'no-cache',
    variables: {
      taskIds: ids,
      queryData: {
        groupBy,
        dateRange,
        validityHints,
      },
    },
  });

  useEffect(() => {
    if (error) {
      captureException(error);
    }
  }, [error]);

  return { loading, data: data?.tasksTimeline, dataGroupBy: timelineView };
}
